import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "react-i18next";

const STATUSES = ["whatsapp_pending", "placed", "confirmed", "shipped", "delivered", "cancelled"];

const STATUS_COLOR = {
  whatsapp_pending: "bg-emerald-50 text-emerald-800",
  placed: "bg-blue-100 text-blue-700",
  confirmed: "bg-emerald-100 text-emerald-700",
  shipped: "bg-amber-100 text-amber-700",
  delivered: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-red-100 text-red-700",
};

const STATUS_LABEL = {
  whatsapp_pending: "WhatsApp order",
  placed: "Placed",
  confirmed: "Confirmed",
  shipped: "Shipped",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

export default function AdminOrders() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/merchant/orders").then(({ data }) => setItems(data || [])).finally(() => setLoading(false));
  }, []);

  const customerCancel = (o) => ["pending", "approved"].includes(o.cancel_request?.status);
  const counts = useMemo(() => {
    const c = { all: items.length, customer_cancel: items.filter(customerCancel).length };
    STATUSES.forEach((s) => { c[s] = items.filter((o) => o.status === s).length; });
    return c;
  }, [items]);

  const statusLabel = (s) => t(`orders.${s}`, { defaultValue: STATUS_LABEL[s] || s });
  const filters = [
    { id: "all", label: t("common.all") },
    ...STATUSES.map((s) => ({ id: s, label: statusLabel(s) })),
    { id: "customer_cancel", label: "Cancelled by customer" },
  ];
  const filtered = status === "all"
    ? items
    : status === "customer_cancel"
      ? items.filter(customerCancel)
      : items.filter((o) => o.status === status);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900">{t("orders.title")}</h1>
        <p className="text-slate-500 text-sm mt-1">{loading ? t("common.loading") : `${filtered.length} ${status === "all" ? t("orders.total") : statusLabel(status).toLowerCase()}`}</p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="orders-status-filters">
        {filters.map((f) => (
          <button
            key={f.id}
            type="button"
            data-testid={`orders-filter-${f.id}`}
            onClick={() => setStatus(f.id)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              status === f.id
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:text-slate-900"
            }`}
          >
            {f.label}
            <span className={`font-mono-nums text-xs ${status === f.id ? "text-white/80" : "text-slate-400"}`}>{counts[f.id] || 0}</span>
          </button>
        ))}
      </div>

      <Card className="p-0 overflow-hidden border-slate-200/70">
        {loading ? (
          <div className="p-12 text-center text-slate-500 text-sm">{t("common.loading")}</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-500 text-sm" data-testid="orders-empty">
            {items.length === 0 ? t("orders.empty") : t("orders.noneStatus", { status: status === "all" ? "" : statusLabel(status) })}
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filtered.map((o) => (
              <Link to={`/admin/orders/${o.id}`} key={o.id} className="p-4 flex items-center gap-4 hover:bg-slate-50" data-testid={`order-row-${o.order_number}`}>
                <div className="flex-1">
                  <div className="font-mono-nums font-semibold text-slate-900">{o.order_number}</div>
                  <div className="text-xs text-slate-500">{o.customer_name} · {o.items?.length || 0} {t("common.items")} · {new Date(o.created_at).toLocaleString("en-IN")}{o.payment_method === "whatsapp" ? " · WhatsApp" : ""}</div>
                  {o.cancel_request?.reason ? (
                    <div className="text-xs text-amber-800 mt-1" data-testid={`order-cancel-reason-${o.order_number}`}>
                      Cancelled by customer{o.cancel_request.status === "pending" ? " · waiting for you" : o.cancel_request.status === "rejected" ? " · disapproved" : ""}: {o.cancel_request.reason}
                    </div>
                  ) : null}
                </div>
                <div className="text-right">
                  <div className="font-mono-nums font-semibold text-slate-900">₹{o.total?.toLocaleString("en-IN")}</div>
                  <Badge className={`${STATUS_COLOR[o.status] || "bg-slate-100 text-slate-700"} border-0`}>{statusLabel(o.status)}</Badge>
                </div>
              </Link>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
